import { Shape, TShapeOptions } from "../Shape";
import { SVGParsedPathData } from "../../svg/SVGUtil";
import { YZCanvasContext } from "../../canvas";
import { rotatePoint } from "../Matrix";
import { length, toRadian } from "@zyzgroup/core-common";

export type TPoint3DOptions = {
  x?: number;
  y?: number;
  z?: number;
  fl?: number;
  vpX?: number;
  vpY?: number;
  cX?: number;
  cY?: number;
  cZ?: number;
  radius?: number;
} & TShapeOptions;

export class Point3D extends Shape {
  public x: number;
  public y: number;
  public z: number;
  // public w: number; // perspective
  public fl: number;
  public vpX: number;
  public vpY: number;
  public cX: number;
  public cY: number;
  public cZ: number;
  public radius: number;

  constructor(options?: TPoint3DOptions) {
    super(options);
    this.x = options?.x || 0;
    this.y = options?.y || 0;
    this.z = options?.z || 0;
    this.fl = options?.fl || 250;
    this.vpX = options?.vpX || 0;
    this.vpY = options?.vpY || 0;
    this.cX = options?.cX || 0;
    this.cY = options?.cY || 0;
    this.cZ = options?.cZ || 0;
    this.radius = options?.radius || 0;
  }

  toSVGPathData(): SVGParsedPathData[] {
    if (this.radius) {
      return [
        ["M", this.getScreenX(), this.getScreenY() - this.radius],
        ["a", this.radius, this.radius, 0, 1, 1, 0, 2 * this.radius],
        ["a", this.radius, this.radius, 0, 1, 1, 0, -2 * this.radius],
        ["z"]
      ];
    } else {
      return [["M", this.getScreenX(), this.getScreenY()]];
    }
  }

  toCanvasContext(ctx: YZCanvasContext) {
    ctx.beginPath();
    ctx.arc(0, 0, this.radius, 0, Math.PI * 2, true);
    ctx.closePath();
  }

  setVanishingPoint(vpX: number, vpY: number) {
    this.vpX = vpX;
    this.vpY = vpY;
  }

  setCenter(cX: number, cY: number, cZ: number) {
    this.cX = cX;
    this.cY = cY;
    this.cZ = cZ;
  }

  rotateX(degX: number) {
    ({ x: this.y, y: this.z } = rotatePoint(toRadian(degX), this.y, this.z));
  }

  rotateY(degY: number) {
    ({ x: this.x, y: this.z } = rotatePoint(toRadian(degY), this.x, this.z));
  }

  rotateZ(degZ: number) {
    ({ x: this.x, y: this.y } = rotatePoint(toRadian(degZ), this.x, this.y));
  }

  getScreenX() {
    const scale = this.fl / (this.fl + this.z + this.cZ);
    return this.vpX + (this.cX + this.x) * scale;
  }

  getScreenY() {
    const scale = this.fl / (this.fl + this.z + this.cZ);
    return this.vpY + (this.cY + this.y) * scale;
  }

  eq(point: Point3D): boolean {
    return (
      this.x === point.x &&
      this.y === point.y &&
      this.z === point.z &&
      this.radius === point.radius &&
      this.mass === point.mass
    );
  }

  length() {
    return length(this.x, this.y, this.z);
  }

  distance(pt: Point3D): number {
    const dx = pt.x - this.x;
    const dy = pt.y - this.y;
    const dz = pt.z - this.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  scalar(scale: number) {
    this.x *= scale;
    this.y *= scale;
    this.z *= scale;
    return this;
  }

  normalize(len = 1): Point3D {
    const totalLength = this.length();
    if (totalLength) {
      const scale = len / totalLength;
      this.scalar(scale);
    }
    return this;
  }

  clamp(len: number) {
    const totalLength = this.length();
    if (totalLength && totalLength > len) {
      this.scalar(len / totalLength);
    }
    return this;
  }

  cross(pt: Point3D) {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    return {
      x: y * pt.z - z * pt.y,
      y: z * pt.x - x * pt.z,
      z: x * pt.y - y * pt.x
    };
  }

  multiply(pt: Point3D) {
    return {
      x: this.x * pt.x,
      y: this.y * pt.y,
      z: this.z * pt.z
    };
  }

  div(pt: Point3D) {
    return {
      x: this.x / pt.x,
      y: this.y / pt.y,
      z: this.z / pt.z
    };
  }

  add(pt: Point3D) {
    return {
      x: this.x + pt.x,
      y: this.y + pt.y,
      z: this.z + pt.z
    };
  }

  sub(pt: Point3D) {
    return {
      x: this.x - pt.x,
      y: this.y - pt.y,
      z: this.z - pt.z
    };
  }

  dot(pt: Point3D): number {
    return this.x * pt.x + this.y * pt.y + this.z * pt.z;
  }

  reflect(pt: Point3D) {
    const dp = this.dot(pt);
    return {
      x: this.x - 2 * dp * pt.x,
      y: this.y - 2 * dp * pt.y,
      z: this.z - 2 * dp * pt.z
    };
  }
}

export default Point3D;
